import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@devhop/ui";
import { PlusIcon, TrashIcon } from "lucide-react";
import {
  createEmptyRepeaterRow,
  formatRepeaterItemLabel,
  parseRepeaterSettings,
  repeaterCanAddRemove,
  repeaterEffectiveMaxItems,
  repeaterEffectiveMinItems,
  type RepeaterAnswer,
  type RepeaterItemFieldDef,
} from "@/modules/forms/domain/lib/form-repeater-settings";
import type { PublicFormFieldDTO } from "@/modules/forms/presentation/public/types";

type PublicFormRepeaterBlockProps = {
  field: PublicFormFieldDTO;
  rows: RepeaterAnswer;
  onChange: (rows: RepeaterAnswer) => void;
  disabled: boolean;
  fieldError?: string;
  onClearError: () => void;
};

function FieldErrorLine({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-destructive text-xs">{message}</p>;
}

function RepeaterSubFieldInput({
  fieldDef,
  value,
  disabled,
  inputId,
  onChange,
}: {
  fieldDef: RepeaterItemFieldDef;
  value: string;
  disabled: boolean;
  inputId: string;
  onChange: (next: string) => void;
}) {
  if (fieldDef.type === "choice") {
    const options = (fieldDef.options ?? []).filter((o) => o.enabled !== false);
    return (
      <Select value={value} disabled={disabled} onValueChange={onChange}>
        <SelectTrigger id={inputId}>
          <SelectValue placeholder={fieldDef.placeholder ?? "ເລືອກ…"} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  const inputType =
    fieldDef.type === "number"
      ? "number"
      : fieldDef.type === "date"
        ? "date"
        : "text";

  return (
    <Input
      id={inputId}
      type={inputType}
      inputMode={fieldDef.type === "number" && fieldDef.integerOnly ? "numeric" : undefined}
      step={fieldDef.type === "number" && fieldDef.integerOnly ? 1 : undefined}
      min={fieldDef.type === "number" ? fieldDef.min : undefined}
      max={fieldDef.type === "number" ? fieldDef.max : undefined}
      disabled={disabled}
      placeholder={fieldDef.placeholder ?? undefined}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function PublicFormRepeaterBlock({
  field,
  rows,
  onChange,
  disabled,
  fieldError,
  onClearError,
}: PublicFormRepeaterBlockProps) {
  const settings = parseRepeaterSettings(field.settings);
  if (!settings) {
    return (
      <p className="text-destructive text-sm">
        ການຕັ້ງຄ່າຟິວ repeater ບໍ່ຖືກຕ້ອງ
      </p>
    );
  }

  const canAddRemove = repeaterCanAddRemove(settings);
  const minItems = repeaterEffectiveMinItems(settings);
  const maxItems = repeaterEffectiveMaxItems(settings);

  const patchRow = (rowIdx: number, key: string, val: string) => {
    onClearError();
    onChange(
      rows.map((row, i) => (i === rowIdx ? { ...row, [key]: val } : row)),
    );
  };

  const addRow = () => {
    if (rows.length >= maxItems) return;
    onClearError();
    onChange([...rows, createEmptyRepeaterRow(settings)]);
  };

  const removeRow = (rowIdx: number) => {
    if (rows.length <= minItems) return;
    onClearError();
    onChange(rows.filter((_, i) => i !== rowIdx));
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <Label>
          {field.label}
          {field.required ? (
            <span className="text-destructive" aria-hidden>
              {" "}
              *
            </span>
          ) : null}
        </Label>
        {field.description ? (
          <p className="mt-1 text-muted-foreground text-xs">{field.description}</p>
        ) : null}
        {field.settings?.helperText ? (
          <p className="mt-1 text-muted-foreground text-xs">
            {field.settings.helperText}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-4">
        {rows.map((row, rowIdx) => {
          const itemLabel =
            settings.showItemNumber !== false
              ? formatRepeaterItemLabel(settings.itemLabelTemplate, rowIdx + 1)
              : null;
          return (
            <div
              key={rowIdx}
              className="space-y-3 rounded-md border border-border/80 p-3"
            >
              <div className="flex items-center justify-between gap-2">
                {itemLabel ? (
                  <p className="font-medium text-sm">{itemLabel}</p>
                ) : (
                  <span />
                )}
                {canAddRemove && rows.length > minItems ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    onClick={() => removeRow(rowIdx)}
                  >
                    <TrashIcon className="mr-1 h-4 w-4" />
                    ລຶບ
                  </Button>
                ) : null}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {settings.itemFields.map((fieldDef) => {
                  const inputId = `pf-${field.id}-${rowIdx}-${fieldDef.key}`;
                  return (
                    <div key={fieldDef.key} className="flex flex-col gap-1.5">
                      <Label htmlFor={inputId} className="text-xs">
                        {fieldDef.label}
                        {fieldDef.required ? (
                          <span className="text-destructive" aria-hidden>
                            {" "}
                            *
                          </span>
                        ) : null}
                      </Label>
                      <RepeaterSubFieldInput
                        fieldDef={fieldDef}
                        value={row[fieldDef.key] ?? ""}
                        disabled={disabled}
                        inputId={inputId}
                        onChange={(val) => patchRow(rowIdx, fieldDef.key, val)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {canAddRemove && rows.length < maxItems ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          disabled={disabled}
          onClick={addRow}
        >
          <PlusIcon className="mr-1 h-4 w-4" />
          ເພີ່ມລາຍການ
        </Button>
      ) : null}

      <FieldErrorLine message={fieldError} />
    </div>
  );
}

export function initRepeaterValsForFields(
  fields: PublicFormFieldDTO[],
): Record<string, RepeaterAnswer> {
  const out: Record<string, RepeaterAnswer> = {};
  for (const f of fields) {
    if (f.type !== "repeater") continue;
    const settings = parseRepeaterSettings(f.settings);
    if (!settings) continue;
    const min = repeaterEffectiveMinItems(settings);
    const initialCount =
      settings.countMode === "fixed"
        ? min
        : Math.max(min || 0, 1);
    out[f.id] = Array.from({ length: initialCount }, () =>
      createEmptyRepeaterRow(settings),
    );
  }
  return out;
}
